import { mapGetters } from 'vuex'
import Vue from 'vue'

const eventBus = new Vue()
const eventRegistrations = {}

var mixins = {
    data() {
        return {
            loading: false,
            response_interceptor: null,
            INTEREST_TYPES: {
                1: { id: 1, description: 'Sleep' },
                2: { id: 2, description: 'Work' },
                3: { id: 3, description: 'Purpose' },
                4: { id: 4, description: 'De-Stress' },
                5: { id: 5, description: 'Self-Care' },
                6: { id: 6, description: 'Focus' },
                7: { id: 7, description: 'Relationships' },
                8: { id: 8, description: 'Soundscapes' }
            }
        }
    },
    watch: {
        loading(val) {
            if (val) {
                this.startLoad(this.$el)
            } else {
                this.endLoad(this.$el)
            }
        }
    },
    beforeDestroy() {
        if (eventRegistrations[this._uid]) {
            eventRegistrations[this._uid].forEach((e) => {
                this.globalOff(e.name, e.handler)
            })
            delete eventRegistrations[this._uid]
        }
    },
    methods: {
        globalEmit(name, payload) {
            eventBus.$emit(name, payload)
        },
        globalOn(name, handler) {
            var eventList = eventRegistrations[this._uid]
            if (!eventList) {
                eventList = eventRegistrations[this._uid] = []
            }
            eventList.push({ name, handler })
            eventBus.$on(name, handler)
        },
        globalOff(name, handler) {
            var eventList = eventRegistrations[this._uid]
            if (!eventList) {
                return
            }
            if (handler) {
                eventList.removeWhere((e) => e.name === name && e.handler === handler)
                eventBus.$off(name, handler)
            } else {
                eventList.removeWhere((e) => e.name === name)
                eventBus.$off(name)
            }
        },
        notifyServerError(error) {
            if (error.response) {
                var code = error.response.status
                // these are not server errors, more likely user errors aside from 503
                if (code == 401 || code == 403) {
                    return
                }

                if (code == 503) {
                    this.notifyError(
                        'Server Unavailable',
                        'CRM is down, this should only be temporary, please try again in a few minutes.',
                        10000
                    )
                    return
                }

                var title = `${code} - ${error.response.statusText}`
                var description = error.response.data.title
                // this is necessary to communicate between the DOM and the store
                window.bugReport = error.response.data
                this.notifyError(
                    title,
                    description +
                        `</br></br><a href="javascript:window.startBugReport();">Click to submit ticket</a>`,
                    null
                )
            }
        },
        userHasRole(roles) {
            return this.$store.getters['auth/has_role'](roles)
        },
        delayFor(millis) {
            return new Promise((resolve, reject) => {
                setTimeout(resolve, millis)
            })
        },
        deepCopy(obj) {
            return JSON.parse(JSON.stringify(obj))
        },
        startLoad(el) {
            if (el) {
                this.$vs.loading({ container: el })
            } else {
                this.$vs.loading()
            }
        },
        watch(var_to_watch) {
            return new Promise(
                function (resolve) {
                    const watcher = this.$watch(
                        var_to_watch,
                        function (newVal) {
                            resolve(newVal)
                            watcher()
                        }.bind(this)
                    )
                }.bind(this)
            )
        },
        async validate() {
            var results = await Promise.all(this.validators.map((v) => v.validate()))
            return results.all((v) => v)
        },
        areAllElementsValidate() {
            this.validationFields.forEach((el) => {
                if (!el.flags.valid) {
                    return false
                }
                return true
            })
        },
        areAnyElementsDirty() {
            this.validationFields.forEach((el) => {
                if (!el.flags.dirty) {
                    return false
                }
                return true
            })
        },
        async endLoad(el) {
            // vuesax bug, if close happens too soon after start it never closes the loader. Possibly
            // due to the virtual dom not being pushed to live yet, despite nextTick() not working
            await this.delayFor(250)
            if (el) {
                this.$vs.loading.close(el)
            } else {
                this.$vs.loading.close()
            }
        },
        notifyError(title, text, delay, onClick) {
            this.$vs.notify({
                title: title,
                text: text,
                iconPack: 'feather',
                icon: 'icon-alert-circle',
                color: 'danger',
                fixed: !delay,
                time: delay,
                click: onClick
            })
        },
        notifySuccess(title, text, delay, onClick) {
            this.$vs.notify({
                title: title,
                text: text,
                iconPack: 'feather',
                icon: 'icon-check-triangle',
                color: 'success',
                fixed: !delay,
                time: delay,
                click: onClick
            })
        },
        notifyWarning(title, text, delay, onClick) {
            this.$vs.notify({
                title: title,
                text: text,
                iconPack: 'feather',
                icon: 'icon-check-square',
                color: 'warning',
                fixed: !delay,
                time: delay,
                click: onClick
            })
        },
        confirm(options) {
            return new Promise((resolve, reject) => {
                this.$vs.dialog({
                    color: 'danger',
                    title: 'Confirm',
                    ...options, // Override the above paramters, but not the below ones
                    type: 'confirm',
                    accept: () => {
                        try {
                            if (options.accept && typeof options.accept === 'function') {
                                options.accept()
                            }
                        } finally {
                            resolve(true)
                        }
                    },
                    cancel: () => resolve(false)
                })
            })
        },
        toggleSideBar() {
            this.$emit('toggle-side-bar')
        },
        getInterestDescription(interest) {
            let id = interest
            if (typeof interest === 'object') {
                id = interest.id
            }
            return this.INTEREST_TYPES[id].description
        },
        getMultipleInterestDescription(interests) {
            if (interests && interests.length > 0) {
                return interests.map((value) => this.getInterestDescription(value)).join('; ')
            }
            return ''
        }
    },
    computed: {
        ...mapGetters({
            authenticated: 'auth/is_authenticated',
            user: 'auth/current_user'
        }),
        validators() {
            const getChildValidators = function (component) {
                var result = []
                component.$children.forEach((child) => {
                    result = result.concat(getChildValidators(child))
                })
                if (component.$validator && component.$validator.fields.length) {
                    result.push(component.$validator)
                }
                return result
            }
            return getChildValidators(this)
        },

        validationFields() {
            var fields = []
            this.validators.forEach((v) => {
                if (v.fields.length > 0) {
                    fields = fields.concat(v.fields.items)
                }
            })

            return fields
        }
    }
}

export default mixins
